import type { Content } from 'pdfmake/interfaces.js';
import { offsetLabel } from '../../inputs/lt-utc.js';
import {
  pdfBlock,
  pdfFacts,
  pdfHeading,
  pdfLtUtc,
  pdfText,
  type RecordPdfApproval,
  type RecordPdfT,
  recordPdf,
} from '../reports/record-pdf.js';
import type { ReportBrand } from '../voyage/source.js';
import type { PortCall } from './types.js';

/**
 * Liman aramasının PDF'i (ADR 0062 §3; M15e'den kalan): liman, sefer, zamanlar (ETA/ETD/ATA/ATD
 * LT + UTC), süre, acente, faaliyetler, notlar, iptal gerekçesi. Liman aramasının onay adımı yoktur
 * (ADR 0052); onay bilgisi yerine kayıt bilgisi: giren (ad, o günkü gemi rolü, zaman) ve son
 * güncelleme.
 */
export interface PortCallPdfInput {
  call: PortCall;
  vessel: { name: string; imo?: string | null };
  brand: ReportBrand | null;
  printedAt: string;
  /** Ülke adı (arayüz dilinde); verilmezse kod. */
  countryName?: string;
}

/** Numara: kayıt kimliği yerine okunur bilgi (liman ve ETA günü). */
export function portCallNumber(call: PortCall): string {
  return `${call.portCode ?? call.portName} ${call.eta.slice(0, 10)}`;
}

export function portCallPdf(t: RecordPdfT, input: PortCallPdfInput) {
  const { call, vessel } = input;
  const f = (key: string) => t(`portCalls.fields.${key}`);
  const p = (key: string, options?: Record<string, unknown>) => t(`portCalls.pdf.${key}`, options);
  const offset = call.tzOffsetMin;
  const hours = call.durationHours;
  const duration = `${Math.floor(hours / 24)}${t('portCalls.units.day')} ${Math.round(hours % 24)}${t('portCalls.units.hour')}${call.durationActual ? '' : ` (${t('portCalls.list.estimated')})`}`;
  const content: Content[] = [
    pdfFacts([
      [f('vessel'), vessel.imo ? `${vessel.name} (IMO ${vessel.imo})` : vessel.name],
      [f('voyage'), call.voyageNo ?? t('portCalls.detail.noVoyage')],
      [f('port'), call.portCode ? `${call.portName} (${call.portCode})` : call.portName],
      [f('country'), input.countryName ?? call.country],
      [f('locationType'), t(`portCalls.locationTypes.${call.locationType}`)],
      [f('berth'), pdfText(call.berth)],
      [p('status'), t(`portCalls.statuses.${call.status}`)],
      [f('timeZone'), offsetLabel(offset)],
    ]),
    pdfHeading(p('times')),
    pdfFacts([
      [f('eta'), pdfLtUtc(call.eta, offset)],
      [f('etd'), pdfLtUtc(call.etd, offset)],
      [f('ata'), pdfLtUtc(call.ata, offset)],
      [f('atd'), pdfLtUtc(call.atd, offset)],
      [f('duration'), duration],
    ]),
    pdfHeading(t('portCalls.detail.agent')),
    pdfFacts([
      [f('agentName'), pdfText(call.agentName)],
      [f('agentContact'), pdfText(call.agentContact)],
      [f('agentPhone'), pdfText(call.agentPhone)],
      [f('agentEmail'), pdfText(call.agentEmail)],
    ]),
    pdfHeading(t('portCalls.detail.activities')),
    {
      text:
        call.activities.length > 0
          ? call.activities.map((a) => t(`portCalls.activities.${a}`)).join(', ')
          : t('portCalls.detail.noActivities'),
    },
    ...pdfBlock(t('portCalls.detail.notes'), call.notes),
    ...(call.cancelledAt
      ? [
          pdfHeading(p('cancellation')),
          pdfFacts([
            [p('cancelledAt'), pdfLtUtc(call.cancelledAt, offset)],
            [p('reason'), pdfText(call.cancelReason)],
          ]),
        ]
      : []),
    { text: p('documents', { count: call.documents }), style: 'muted', margin: [0, 6, 0, 0] },
  ];
  const approvals: RecordPdfApproval[] = [
    {
      step: p('recorded'),
      name: call.createdByName ?? null,
      role: call.createdByRole ?? null,
      at: call.createdAt,
      offsetMin: offset,
    },
  ];
  if (call.updatedAt !== call.createdAt) {
    approvals.push({
      step: p('updated'),
      name: null,
      role: null,
      at: call.updatedAt,
      offsetMin: offset,
    });
  }
  const marks =
    call.status === 'cancelled' ? [t('portCalls.statuses.cancelled').toUpperCase()] : [];
  return recordPdf(t, {
    brand: input.brand,
    title: p('title', { port: call.portName }),
    number: portCallNumber(call),
    marks,
    subject: vessel.name,
    content,
    approvals,
    printedAt: input.printedAt,
  });
}
